import * as React from 'react';
import { cn } from '../lib/utils';

const statusBorderColors: Record<string, string> = {
  success: 'border-l-success',
  warning: 'border-l-warning',
  danger: 'border-l-danger',
  info: 'border-l-info',
};

export interface StatCardProps extends React.ComponentPropsWithoutRef<'div'> {
  label: string;
  value: React.ReactNode;
  detail?: string;
  status?: 'default' | 'success' | 'warning' | 'danger' | 'info';
}

export function StatCard({
  label,
  value,
  detail,
  status,
  className,
  children,
  ...props
}: StatCardProps) {
  const borderClass = status && status !== 'default' ? statusBorderColors[status] : undefined;

  return (
    <div
      className={cn(
        'min-w-0',
        borderClass && `border-l-2 pl-3 ${borderClass}`,
        className
      )}
      {...props}
    >
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-metric tabular-nums text-foreground">{value}</p>
      {detail ? <p className="mt-1.5 text-xs text-muted-foreground">{detail}</p> : null}
      {children}
    </div>
  );
}

const gridColClasses: Record<number, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-2 lg:grid-cols-4',
};

export interface MetricGridProps extends React.ComponentPropsWithoutRef<'div'> {
  columns?: 2 | 3 | 4;
}

export function MetricGrid({
  columns,
  className,
  children,
  ...props
}: MetricGridProps) {
  const cols = columns ?? Math.max(2, Math.min(4, React.Children.toArray(children).length));
  const responsiveClass = gridColClasses[cols] ?? gridColClasses[3];

  return (
    <div className={cn('grid grid-cols-1 gap-x-6 gap-y-5', responsiveClass, className)} {...props}>
      {children}
    </div>
  );
}
